import { HttpHeaders } from '@angular/common/http';

/** Headers whose value is a credential and must never reach the log stream in full. */
const SECRET_HEADERS = ['authorization', 'x-api-key'];

/** Tail kept visible: enough to match a token across log lines, too little to be reusable. */
const VISIBLE_SUFFIX_LENGTH = 6;

/**
 * Masks a credential, keeping the auth scheme and the last {@link VISIBLE_SUFFIX_LENGTH}
 * characters so the same token can still be correlated between log lines:
 * `Bearer eyJhbGciOi...Yjk4M2Y` -> `Bearer ***Yjk4M2Y`.
 *
 * A short value would give away too large a share of itself, so nothing is revealed.
 */
export const maskSecret = (value: string | null | undefined): string => {
  if (!value) return '';

  const separatorIndex = value.indexOf(' ');
  const scheme = separatorIndex > 0 ? value.slice(0, separatorIndex + 1) : '';
  const secret = separatorIndex > 0 ? value.slice(separatorIndex + 1) : value;
  const visible = secret.length > VISIBLE_SUFFIX_LENGTH * 2 ? secret.slice(-VISIBLE_SUFFIX_LENGTH) : '';

  return `${scheme}***${visible}`;
};

/**
 * Flattens request headers into a plain `name: value` object for logging, with credentials masked.
 *
 * Read through the public `keys()`/`get()` API on purpose: serialising an `HttpHeaders` instance
 * directly (e.g. via `ignoreObjectProps`) exposes its internal `lazyUpdate` queue, which carries
 * every appended header — including the raw `Authorization` value.
 */
export const headersForLogs = (headers: HttpHeaders | undefined): Record<string, string> => {
  if (!headers) return {};

  return headers.keys().reduce<Record<string, string>>((result, name) => {
    const value = headers.get(name);
    result[name] = SECRET_HEADERS.includes(name.toLowerCase()) ? maskSecret(value) : value ?? '';
    return result;
  }, {});
};
